/**
 * Manos string registry. Every customer-facing string lives in
 * `./en.ts`; this file is the typed accessor layer.
 *
 * Why a registry rather than inline strings:
 *
 *   1. Centralises the voice. The doc at `docs/voice-and-tone.md`
 *      is the rulebook; this file is the canonical implementation.
 *      A copy review now reads one file instead of grepping JSX.
 *
 *   2. i18n-ready without committing to a full ICU pipeline yet.
 *      When the second locale ships (Spanish / French Canadian),
 *      we add `./es.ts`, `./fr-CA.ts` etc. and `setLocale()` flips
 *      the active table. The `t()` call sites don't change.
 *
 *   3. Type-safe. `t("tour.welcome.title")` autocompletes through
 *      the nested keys and errors at compile time on a typo.
 *
 * What this is NOT:
 *
 *   - A full i18n library. No ICU MessageFormat, no plural rules
 *     beyond English-style (one / many), no gender / context
 *     selectors. We'll lift to Lingui or i18next when the second
 *     locale actually needs those.
 *
 *   - A runtime CMS. Strings ship in the bundle.
 *
 *   - A place for JSX. Strings are TEXT only; bold / link wrapping
 *     happens at the call site so translators only see plain text.
 *
 *   - A place for dates / money / numbers. Those are formatted by
 *     `@barrykilby/geo` formatters and inlined via `{{name}}`
 *     placeholders.
 *
 * Lift target: when the API has stabilised in Manos for a few
 * weeks, the helpers below get extracted to `@barrykilby/i18n` for
 * sibling apps (Benchmark, LeisureHub, FreeTime, HouseProud) to
 * adopt. The strings stay per-app -- each product has its own voice
 * and its own audience.
 */

import * as React from "react";
import { en, type Strings } from "./en";
import { enUS } from "./en-US";
import { enCA } from "./en-CA";

// ─── Types ─────────────────────────────────────────────────────

/**
 * Recursive path through the strings tree, dot-joined. e.g.
 *   "tour.welcome.title" | "buttons.newQuote" | "errors.loadFailed"
 *
 * The conditional `T[K] extends string` short-circuits when we hit
 * a leaf so the union doesn't include intermediate object keys.
 */
export type StringPath<T, Prefix extends string = ""> = {
  [K in keyof T & string]: T[K] extends string
    ? `${Prefix}${K}`
    : T[K] extends Record<string, unknown>
      ? StringPath<T[K], `${Prefix}${K}.`>
      : never;
}[keyof T & string];

export type StringKey = StringPath<Strings>;

/** Deep-partial of the canonical Strings tree. Override locales
 *  (en-US, en-CA, future es / fr-CA) only need to declare the keys
 *  that differ from `en`; the t() lookup chain falls back to `en`
 *  for everything missing. */
export type StringOverrides = {
  [K in keyof Strings]?: Strings[K] extends string
    ? string
    : { [K2 in keyof Strings[K]]?: Strings[K][K2] extends string
        ? string
        : { [K3 in keyof Strings[K][K2]]?: string }
      }
};

/** A locale code we know how to load. Add new locales to this union
 *  AND add the matching table import + entry in `tables` below. */
export type Locale = "en" | "en-US" | "en-CA";

// ─── Locale state ──────────────────────────────────────────────

const tables: { en: Strings; "en-US": StringOverrides; "en-CA": StringOverrides } = {
  en,
  "en-US": enUS,
  "en-CA": enCA,
};

/**
 * Active locale. Mutable module-level state -- intentionally simple
 * since SSR hydrates with the visitor's locale via geo, then the
 * client carries on with the same value. If a future surface needs
 * per-component locale (translation preview tooling, the help
 * centre showing all locales side-by-side), wrap in a React
 * context at that point; the useT() hook below already pulls from
 * this module so the swap is one file.
 */
let activeLocale: Locale = "en";

export function setLocale(locale: Locale): void {
  activeLocale = locale;
}

export function getLocale(): Locale {
  return activeLocale;
}

/** Resolve the active Locale from a visitor's country code (ISO
 *  3166-1 alpha-2). Defaults to base "en" for any country not
 *  in the explicit override map -- so GB / IE / AU / NZ / unknown
 *  all get the canonical en strings. US and CA get their own
 *  variant tables. Extend as more locales ship. */
export function localeForCountry(country: string | null | undefined): Locale {
  if (!country) return "en";
  const upper = country.toUpperCase();
  if (upper === "US") return "en-US";
  if (upper === "CA") return "en-CA";
  return "en";
}

/** Convenience: combine localeForCountry + setLocale. Call once at
 *  app boot (root.tsx) with the visitor / trader country. */
export function setLocaleForCountry(country: string | null | undefined): void {
  setLocale(localeForCountry(country));
}

// ─── Translation accessor ──────────────────────────────────────

/**
 * Pure lookup -- pass an explicit locale, get the string back. Used
 * by:
 *   - the module-level t() below (defaults to activeLocale)
 *   - useT() via React context (per-request locale)
 *   - server-side email templates (pass the trader's locale)
 *
 * Fallback chain: locale-specific table -> en -> the key itself
 * (with a dev-console warning). Means a partial override never
 * blanks out the UI.
 */
export function tForLocale(
  locale: Locale,
  key: StringKey,
  vars?: Record<string, string | number>,
): string {
  const value = lookup(tables[locale], key) ?? lookup(en, key);
  if (value == null) {
    if (typeof console !== "undefined") {
      // eslint-disable-next-line no-console
      console.warn(`[strings] missing key: ${key}`);
    }
    return key;
  }
  if (!vars) return value;
  return value.replace(/\{\{(\w+)\}\}/g, (match, name: string) => {
    const v = vars[name];
    return v == null ? match : String(v);
  });
}

/**
 * Module-level convenience. Reads from `activeLocale` (set via
 * setLocale). Safe to call from non-React code (utility modules,
 * server-side handlers); React components should prefer useT().
 *
 * Example:
 *   t("toast.invoiceSent", { customerName: "Mrs Patterson" })
 *   -> "Invoice sent to Mrs Patterson. We'll let you know when they open it."
 */
export function t(
  key: StringKey,
  vars?: Record<string, string | number>,
): string {
  return tForLocale(activeLocale, key, vars);
}

function lookup(table: unknown, key: string): string | undefined {
  const segments = key.split(".");
  let node: unknown = table;
  for (const segment of segments) {
    if (node == null || typeof node !== "object") return undefined;
    node = (node as Record<string, unknown>)[segment];
  }
  return typeof node === "string" ? node : undefined;
}

// ─── Plural helper ─────────────────────────────────────────────

/**
 * English-style plural picker. Two forms only -- one (n === 1) and
 * many (everything else, including 0).
 *
 *   plural(items.length, "invoice", "invoices")
 *   plural(count, "1 quote", `${count} quotes`)
 *
 * Locales with more plural categories (Russian, Polish, Arabic etc)
 * will need a richer helper or a real ICU runtime; flagged in the
 * lift-to-@barrykilby/i18n design notes.
 */
export function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

// ─── React context + hook ──────────────────────────────────────

/**
 * Provider context for the active locale. Lives at root.tsx so SSR
 * sees the visitor's resolved locale and renders the right strings
 * the first time, avoiding hydration mismatches (which is what
 * module-level state would cause: SSR with default 'en' vs client
 * with 'en-US' produces different text nodes).
 *
 * The context value is the Locale string. useT() reads it and
 * returns a t() closure scoped to that locale.
 */
const LocaleContext = React.createContext<Locale>("en");

/** Wrap the React tree at the root so descendant useT() calls see
 *  the right locale. The locale prop is computed in the root loader
 *  via localeForCountry(visitorGeo.country | profile.country). */
export function LocaleProvider({
  locale,
  children,
}: {
  locale: Locale;
  children: React.ReactNode;
}) {
  return React.createElement(
    LocaleContext.Provider,
    { value: locale },
    children,
  );
}

/**
 * React-side translation hook. Reads the active locale from
 * LocaleContext and returns a t() closure scoped to that locale.
 * Use this in components instead of the module-level t().
 *
 *   const t = useT();
 *   <p>{t("tour.welcome.title")}</p>
 */
export function useT(): (
  key: StringKey,
  vars?: Record<string, string | number>,
) => string {
  const locale = React.useContext(LocaleContext);
  return React.useCallback(
    (key, vars) => tForLocale(locale, key, vars),
    [locale],
  );
}

/** Read the React-context locale. Useful for non-string surfaces
 *  that depend on locale (e.g. picking a date formatter). */
export function useLocaleContext(): Locale {
  return React.useContext(LocaleContext);
}

// ─── Re-exports ────────────────────────────────────────────────

export { en } from "./en";
export type { Strings } from "./en";
